/**
 * useAuth — Keycloak OAuth PKCE authentication for Office add-ins.
 * Host-agnostic; shared by every add-in. (Canonical version — was most current
 * in usable-excel; usable-word had drifted and is uplifted to this.)
 *
 * Flow:
 *  1. On mount: try to restore session from cached refresh token (silent).
 *  2. If no session: state = "unauthenticated" → caller shows a login button.
 *  3. login() opens the Office Dialog which runs the PKCE flow.
 *  4. On success: token cached in localStorage, state = "authenticated".
 *  5. Silent refresh scheduled 30 s before expiry via the Keycloak proxy.
 *
 * Robustness (concurrent refreshes, transient failures) is centralised here so
 * every add-in that consumes this hook inherits it.
 */

import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Hit Keycloak directly (not a webpack proxy path): the proxy only exists in the
// dev server, so in the hosted static build a relative "/auth/token" 404s → the
// refresh looks like a genuine auth failure and the stored refresh token gets
// cleared, forcing re-login on every reopen. The login dialog already POSTs to
// this same absolute URL cross-origin successfully (the app's Keycloak client
// lists every add-in origin under Web Origins).
const KC_TOKEN_URL = "https://auth.flowcore.io/realms/memory-mesh/protocol/openid-connect/token";

// The "Usable for Microsoft Office" marketplace application — a public (SPA +
// PKCE) Keycloak client, so there is no secret to ship here. It replaces the
// shared mcp_oauth_client: Usable classifies that one as `mcp-oauth`, which the
// credit preflight refuses regardless of scopes, so chat generations failed.
//
// NOTE: CLIENT_ID and this Keycloak token endpoint are intentionally also
// hard-coded in the standalone auth-dialog.html (served as a static page, so it
// cannot import this module) — both now hit Keycloak directly. Keep the two in
// sync if the OAuth client/realm change.
const CLIENT_ID = "0bcf1f10-dc8c-4561-b5d3-05725d19388b";

/** localStorage key for the Keycloak refresh token */
const RT_KEY = "usableRefreshToken";

/** Fallback access-token lifetime (seconds) when the IdP omits expires_in. */
const DEFAULT_TOKEN_LIFETIME_SECONDS = 300;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AuthState = "restoring" | "unauthenticated" | "authenticated";

export interface AuthResult {
  state: AuthState;
  accessToken: string | null;
  login: () => void;
  logout: () => void;
  /** Fetch a fresh access token using the stored refresh token. */
  refreshAccessToken: () => Promise<string | null>;
  /** Return current token if valid for >60s, otherwise refresh. */
  ensureValidToken: () => Promise<string | null>;
}

/**
 * Result of a refresh-token exchange. We distinguish a *genuine* auth failure
 * (the refresh token is invalid — drop it and force re-login) from a *transient*
 * failure (network blip / 5xx — keep the token and retry later), so a momentary
 * hiccup never strands the session.
 */
type RefreshOutcome =
  | { ok: true; accessToken: string; refreshToken: string | null; expiresIn: number }
  | { ok: false; transient: boolean };

// ---------------------------------------------------------------------------
// Token exchange helpers
// ---------------------------------------------------------------------------

async function exchangeRefreshToken(rt: string): Promise<RefreshOutcome> {
  const body = new URLSearchParams({
    grant_type:    "refresh_token",
    client_id:     CLIENT_ID,
    refresh_token: rt,
  });

  let res: Response;
  try {
    res = await fetch(KC_TOKEN_URL, {
      method:  "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body:    body.toString(),
    });
  } catch {
    // Network / DNS / TLS failure — common in an Office WebView that just
    // regained focus. Transient: keep the refresh token for a later retry.
    return { ok: false, transient: true };
  }

  if (!res.ok) {
    // 4xx → the refresh token is genuinely invalid (e.g. invalid_grant);
    // 5xx → transient proxy/server hiccup, keep the RT for retry.
    return { ok: false, transient: res.status >= 500 };
  }

  const tokens = await res.json() as {
    access_token:  string;
    refresh_token?: string;
    expires_in?:   number;
  };

  return {
    ok:           true,
    accessToken:  tokens.access_token,
    refreshToken: tokens.refresh_token ?? null,
    expiresIn:    tokens.expires_in    ?? DEFAULT_TOKEN_LIFETIME_SECONDS,
  };
}

function saveRefreshToken(rt: string) {
  try {
    localStorage.setItem(RT_KEY, rt);
  } catch {
    // private browsing or storage quota
  }
}

function clearRefreshToken() {
  try {
    localStorage.removeItem(RT_KEY);
  } catch {
    // ignore
  }
}

function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(RT_KEY);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthResult {
  const [state, setState]             = useState<AuthState>("restoring");
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const refreshTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogRef         = useRef<Office.Dialog | null>(null);
  const tokenExpiresAtRef = useRef<number | null>(null);
  const accessTokenRef    = useRef<string | null>(null);
  accessTokenRef.current  = accessToken;

  // Collapses concurrent refreshes onto a single in-flight promise so a rotating
  // refresh token is never double-spent (which would 400 → invalid_grant → logout).
  const inFlightRef = useRef<Promise<string | null> | null>(null);
  // Indirection so applyTokens can schedule a refresh without a useCallback cycle.
  const refreshAccessTokenRef = useRef<() => Promise<string | null>>();

  // -------------------------------------------------------------------------
  // Schedule silent token refresh
  // -------------------------------------------------------------------------

  const scheduleRefresh = useCallback((expiresIn: number, doRefresh: () => void) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max((expiresIn - 30) * 1000, 10_000);
    refreshTimerRef.current = setTimeout(doRefresh, delay);
  }, []);

  // -------------------------------------------------------------------------
  // applyTokens — the single "we have valid tokens now" transition, shared by
  // login() and refreshAccessToken() so they can't drift.
  // -------------------------------------------------------------------------

  const applyTokens = useCallback((accessToken: string, refreshToken: string | null, expiresIn: number) => {
    setAccessToken(accessToken);
    setState("authenticated");
    tokenExpiresAtRef.current = Date.now() + expiresIn * 1000;
    scheduleRefresh(expiresIn, () => { void refreshAccessTokenRef.current?.(); });
    if (refreshToken) saveRefreshToken(refreshToken);
  }, [scheduleRefresh]);

  // -------------------------------------------------------------------------
  // refreshAccessToken (exported so useChatEmbed can call it on demand)
  // -------------------------------------------------------------------------

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // De-dup concurrent callers onto one in-flight exchange.
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async (): Promise<string | null> => {
      const rt = getStoredRefreshToken();
      if (!rt) return null;

      const outcome = await exchangeRefreshToken(rt);
      if (!outcome.ok) {
        // Only drop the refresh token on a *genuine* auth failure. Transient
        // failures (network / 5xx) keep it so a blip doesn't force re-login.
        if (!outcome.transient) clearRefreshToken();
        return null;
      }

      applyTokens(outcome.accessToken, outcome.refreshToken, outcome.expiresIn);
      return outcome.accessToken;
    })();

    inFlightRef.current = run;
    try {
      return await run;
    } finally {
      inFlightRef.current = null;
    }
  }, [applyTokens]);

  // Keep the ref pointing at the latest refreshAccessToken for applyTokens' timer.
  refreshAccessTokenRef.current = refreshAccessToken;

  // -------------------------------------------------------------------------
  // ensureValidToken — return current token if still fresh, else refresh
  // -------------------------------------------------------------------------

  const ensureValidToken = useCallback(async (): Promise<string | null> => {
    const expiresAt    = tokenExpiresAtRef.current;
    const currentToken = accessTokenRef.current;

    // Token is still valid for more than 60 seconds — return it without a network call
    if (currentToken && expiresAt && (expiresAt - Date.now()) > 60_000) {
      return currentToken;
    }

    // Token is expired or expiring soon — fetch a fresh one
    return refreshAccessToken();
  }, [refreshAccessToken]);

  // -------------------------------------------------------------------------
  // Restore session on mount
  // -------------------------------------------------------------------------

  useEffect(() => {
    refreshAccessToken()
      .then((token) => { if (!token) setState("unauthenticated"); })
      .catch(() => setState("unauthenticated")); // never strand on "restoring"

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [refreshAccessToken]);

  // -------------------------------------------------------------------------
  // Refresh token when the task pane becomes visible again.
  // In Office add-in WebViews (WebView2/WKWebView), setTimeout is throttled
  // while the add-in is hidden, so the proactive timer may fire late or not at
  // all. ensureValidToken() on visibility restore covers that throttled-timer
  // case (it refreshes if the token is near expiry) WITHOUT spending a refresh
  // token rotation on every focus when the token is still fresh.
  // -------------------------------------------------------------------------

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) {
        void ensureValidToken();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [ensureValidToken]);

  // -------------------------------------------------------------------------
  // login — open Office Dialog with PKCE flow
  // -------------------------------------------------------------------------

  const login = useCallback(() => {
    const basePath = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/') + 1);
    const dialogUrl = `${window.location.origin}${basePath}auth-dialog.html`;

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 40, displayInIframe: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error("[Auth] Failed to open login dialog:", result.error.message);
          return;
        }

        const dialog = result.value;
        dialogRef.current = dialog;

        // Receive token (or error) from the dialog page
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (args: { message: string; origin: string | undefined } | { error: number }) => {
            if (!("message" in args)) return; // dialog error event, not a message
            let data: { type: string; accessToken?: string; refreshToken?: string; expiresIn?: number; error?: string };
            try {
              data = JSON.parse(args.message) as typeof data;
            } catch {
              return;
            }

            if (data.type === "AUTH_SUCCESS" && data.accessToken) {
              dialog.close();
              dialogRef.current = null;

              applyTokens(
                data.accessToken,
                data.refreshToken ?? null,
                data.expiresIn ?? DEFAULT_TOKEN_LIFETIME_SECONDS,
              );
            } else if (data.type === "AUTH_ERROR") {
              console.error("[Auth] Login dialog error:", data.error);
              dialog.close();
              dialogRef.current = null;
            }
          }
        );

        // Dialog closed by user without completing
        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          () => {
            dialogRef.current = null;
          }
        );
      }
    );
  }, [applyTokens]);

  // -------------------------------------------------------------------------
  // logout
  // -------------------------------------------------------------------------

  const logout = useCallback(() => {
    clearRefreshToken();
    setAccessToken(null);
    setState("unauthenticated");
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  return { state, accessToken, login, logout, refreshAccessToken, ensureValidToken };
}
